<template>
  <v-container class="financial-page">
    <Overview :value="overview" />
    <v-row class="pa-0 ma-0">
      <v-col cols="12" class="pa-0 ma-0">
        <div class="default-table salary-table" style="position: relative;">
          <v-tooltip left>
            <template v-slot:activator="{ on }">
              <v-btn
                v-if="permissions.financial.cash_flow.actions.new"
                v-on="on"
                @click.prevent="newDialog()"
                color="primary"
                class="mb-9"
                app
                fab
                bottom
                right
                fixed
              >
                <v-icon dark>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>{{ lang.financial.salary.new.title }}</span>
          </v-tooltip>
          <v-row>
            <Search name="financial.salary">
              <Filters v-model="filters" />
              <Periodicals subpage="salary" />
            </Search>
          </v-row>
          <v-row>
            <v-col cols="12" lg="3" md="5" sm="12" xs="12" class="stats-col">
              <Info :filters="filters" @clearFilters="clearFilters" />
            </v-col>
            <v-col cols="12" lg="9" md="7" sm="12" xs="12" class="pa-0 ma-0">
              <Save
                v-if="dialog.selected"
                v-model="dialog.selected"
                :id="dialog.selected.id"
                :dialog="dialog.save"
                :key="`save_${(dialog.selected || {}).id}`"
                @closed="onCloseSave"
              />
              <ViewDialog
                v-if="dialog.selected"
                v-model="dialog.selected"
                :id="dialog.selected.id"
                :dialog="dialog.view"
                :fixed="2"
                :key="`view_${(dialog.selected || {}).id}`"
                @closed="onCloseView"
                @edit="editDialog(dialog.selected)"
              />
              <DataTable
                name="financial.salary"
                :items="data"
                :tablehdrs="tableheaders.financial.salary"
                itemkey="id"
                sortby="datetime"
                groupby="date"
                sortdesc
                hideheader
              >
                <template v-slot:groupName="{ group }">
                  {{ formatDateLL(group) }}
                </template>
                <template v-slot:default="{ item }">
                  <tr
                    class="table-row salary-row"
                    :class="(item.type === 1 ? 'income' : 'expense')"
                  >
                    <td>
                      {{ item.id }}
                    </td>
                    <td class="date-col">
                      {{ formatDate(item.datetime) }}
                    </td>
                    <td class="title-col">
                      <span>{{ item.title }}</span>
                    </td>
                    <td class="description-col">
                      <v-chip
                        v-if="item.description"
                        class="description"
                        dark
                      >
                        {{ item.description }}
                      </v-chip>
                    </td>
                    <td>
                      <v-icon
                        v-if="item.type === 1"
                        v-text="`mdi-arrow-up-bold`"
                        color="lime accent-4"
                        class="mb-1"
                        large
                      />
                      <v-icon
                        v-else
                        v-text="`mdi-arrow-down-bold`"
                        color="deep-orange accent-3"
                        large
                      />
                      <span class="text-body-1 font-weight-black">{{ formatCurrency(item.value) }}</span>
                    </td>
                    <td class="actions-col">
                      <Actions
                        :value="item"
                        @view="viewDialog(item)"
                        @edit="editDialog(item)"
                      />
                    </td>
                  </tr>
                </template>
                <template v-slot:mobile="{ item }">
                  <tr class="mobile_row table-row salary-row">
                    <td class="mobile_col">
                      <ul class="flex-content pl-2">
                        <li class="flex-item">ÍCONE</li>
                        <li class="flex-item" :data-label="lang.financial.salary.datatable.headers.datetime">
                          {{ formatDate(item.datetime) }}
                        </li>
                        <li class="flex-item">{{ item.title }}</li>
                        <li class="flex-item description-col">
                          <v-chip class="description" dark>
                            DESCRIÇÃO AQUI
                            {{ item.description }}
                          </v-chip>
                        </li>
                        <li class="flex-item" :data-label="lang.financial.salary.datatable.headers.value">
                          {{ formatCurrency(item.value) }}
                        </li>
                        <li class="flex-item actions-row" :data-label="lang.financial.salary.datatable.headers.actions">
                          <Actions
                            :value="item"
                            @view="viewDialog(item)"
                            @edit="editDialog(item)"
                          />
                        </li>
                      </ul>
                    </td>
                  </tr>
                </template>
              </DataTable>
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Overview from './templates/Overview'
import Search from '@/templates/global/Search'
import Filters from './templates/Filters'
import Periodicals from '../periodicals/Dialog'
import Info from './templates/Info'
import DataTable from '@/templates/global/DataTable'
import Actions from './templates/Actions'
import Save from './Save'
import ViewDialog from '../cashflow/View'

import defaults from '@/modules/default'
import appdata from '@/mixins/appdata'
import utils from '@/mixins/utils'
import filters from '@/mixins/defaults/filters'
import list from '@/mixins/defaults/list_subpage'
import periodicals from '@/components/financial/periodicals/mixins/index'

import moment from 'moment'

export default {
  components: { Overview, Search, Filters, Periodicals, Info, DataTable, Actions, Save, ViewDialog },
  mixins: [ appdata, utils, list, periodicals ],
  data: () => ({
    default: { ...defaults.blank.financial.salary },
    filters: { ...filters.financial.salary }
  }),
  computed: {
    filtered () {
      let data = this.get_data?.financial ?? {},
          salary = (data.expenses || []).filter(exp => exp.category === 1 || exp.category === 5),
          rtrn = [],
          { date } = this.filters || {}
      // DATE FILTER
      rtrn = salary.filter((e) => {
        let created = new Date(e.datetime),
            start = new Date(`${date[0]} 00:00:00`),
            end = new Date(`${date[1]} 23:59:59`)
        return ((isNaN(start.getTime()) || start <= created) && (isNaN(end.getTime()) || end >= created))
      })
      return {
        periodicals: data.periodicals,
        payments: data.payments,
        salary: rtrn
      }
    },
    data () {
      let data = this.filtered?.salary ?? []
      return data.map(el => {
        // el.date = this.formatDateLL((el.datetime || '').split(' ')[0])
        el.date = (el.datetime || '').split(' ')[0]
        return el
      })
    },
    overview () {
      let data = this.filtered || {},
          salary = (data.salary || []).filter(exp => exp.category === 2),
          periodicals = (data.periodicals || []).filter(per => {
            let due = this.getDue(per),
                is_between = moment(due.date).isBetween(this.filters.date[0], this.filters.date[1], undefined, '[]')
            return (per.type === 2 && is_between)
          }),
          total = periodicals.reduce((acc, curr) => parseFloat((acc + curr.value).toFixed(10)), 0),
          paid = salary.reduce((acc, curr) => parseFloat((acc + curr.value).toFixed(10)), 0)
      total = parseFloat(parseFloat(total) + parseFloat(paid))
      let pending = parseFloat(parseFloat(total) - parseFloat(paid))
      return {
        total,
        pending: (pending > 0 ? pending : 0),
        paid
      }
    }
  },
  watch: {
    get_data: {
      handler() {
        this.clearFilters();
      },
      deep: true
    }
  },
  methods: {
    clearFilters () {
      this.filters = { ...filters.financial.salary }
    },
    resetForm () {
      this.dialog.selected = { ...this.default }
      if (this.$refs.SaveForm) this.$refs.SaveForm.resetValidation()
      this.$router.push({ name: `${this.routerPath.root}${(this.routerPath.page && this.routerPath.page !== 'default' ? ':' + this.routerPath.type : '')}`, params: { page: (this.routerPath.page ? this.routerPath.page_path : false) } }).catch(err => { if (err.name !== 'NavigationDuplicated') console.error(err) })
    }
  }
}
</script>

<style src="./styles/index.scss" lang="scss"></style>
